import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { REQUEST_KINDS } from "../lib/comms";
import { Star, Send, ListChecks, Loader2, Building2, Calendar, FileText } from "lucide-react";

/* Legacy /service-orders status labels (partner services) */
const LEGACY_STATUS_LABEL = {
  new: { ar: "جديد", en: "New" },
  in_review: { ar: "قيد المراجعة", en: "In review" },
  accepted: { ar: "تم القبول", en: "Accepted" },
  in_progress: { ar: "جاري التنفيذ", en: "In progress" },
  completed: { ar: "مكتمل", en: "Completed" },
  cancelled: { ar: "ملغي", en: "Cancelled" },
};

/* Phase 2D unified customer-request status labels */
const REQUEST_STATUS_LABEL = {
  open:        { ar: "مفتوح",        en: "Open",        tone: "gold"   },
  in_progress: { ar: "قيد الدراسة",  en: "In review",   tone: "gold"   },
  won:         { ar: "تمت الموافقة", en: "Approved",    tone: "green"  },
  lost:        { ar: "مرفوض",        en: "Rejected",    tone: "red"    },
  closed:      { ar: "مغلق",         en: "Closed",      tone: "gray"   },
};
const TONE = { gold: "#c9a55c", green: "#16a34a", red: "#dc2626", gray: "#8a8a8a" };

const kindLabel = (k, lang) => {
  const found = REQUEST_KINDS.find((x) => x.key === k);
  return found ? (lang === "ar" ? found.ar : found.en) : k;
};

export default function MyOrdersPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [orders, setOrders] = useState([]);          // legacy /service-orders
  const [requests, setRequests] = useState([]);      // Phase 2D /platform/comms/requests/mine
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [review, setReview] = useState({ rating: 5, comment: "" });

  const load = async () => {
    setLoading(true);
    // Fetch both sources in parallel so the customer sees EVERY request they
    // submitted, regardless of which flow (legacy partner-service booking or
    // Phase 2D unified customer-request) it was created through.
    try {
      const [{ data: oData }, { data: rData }] = await Promise.all([
        apiClient.get("/service-orders/mine").catch(() => ({ data: { items: [] } })),
        apiClient.get("/platform/comms/requests/mine?limit=100").catch(() => ({ data: { items: [] } })),
      ]);
      setOrders(oData.items || []);
      setRequests(rData.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (user === undefined) return <div className="p-10 text-[#525252]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/orders" }} replace />;
  if (loading) return <div className="p-10"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const cancel = async (oid) => { await apiClient.patch(`/service-orders/${oid}`, { status: "cancelled" }); load(); };

  const submitReview = async () => {
    try {
      await apiClient.post("/partners/reviews", { partner_id: reviewing.partner_id, order_id: reviewing.id, rating: review.rating, comment: review.comment });
      setReviewing(null);
      setReview({ rating: 5, comment: "" });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Error");
    }
  };

  const totalCount = orders.length + requests.length;

  return (
    <div className="max-w-[1100px] mx-auto px-5 py-10" data-testid="my-orders">
      <div className="overline overline-gold mb-1">{lang === "ar" ? "متابعة الطلبات" : "Order tracking"}</div>
      <h1 className="font-display text-4xl mb-8 flex items-center gap-2">
        <ListChecks className="w-6 h-6 text-[#8a6f2e]" />
        {lang === "ar" ? "طلباتي" : "My orders"} ({totalCount})
      </h1>

      {totalCount === 0 ? (
        <div className="surface rounded-xl p-16 text-center text-[#6b6b6b]">
          <p className="mb-4">{lang === "ar" ? "لم تطلب أي خدمة بعد." : "No orders yet."}</p>
          <Link to="/services/inspection" className="btn-primary mx-auto inline-flex">{lang === "ar" ? "تصفّح الخدمات" : "Browse services"}</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Phase 2D unified requests — finance/insurance/inspection/etc. */}
          {requests.map((r) => {
            const st = REQUEST_STATUS_LABEL[r.status] || REQUEST_STATUS_LABEL.open;
            const color = TONE[st.tone];
            return (
              <div key={r.id} className="surface rounded-xl p-5" data-testid={`my-request-${r.id}`}>
                <div className="flex justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-base text-[#0a0a0a]" data-testid={`my-request-${r.id}-kind`}>
                        {kindLabel(r.kind, lang)}
                      </span>
                      <span className="text-xs text-[#8a8a8a]">·</span>
                      <span className="inline-flex items-center gap-1 text-sm text-[#3a3a3a]">
                        <Building2 className="w-3.5 h-3.5 text-[#8a6f2e]" />
                        <span data-testid={`my-request-${r.id}-biz`}>
                          {lang === "ar" ? (r.business_name_ar || r.business_name_en) : (r.business_name_en || r.business_name_ar)}
                          {!r.business_name_ar && !r.business_name_en && (lang === "ar" ? "جهة الخدمة" : "Service provider")}
                        </span>
                      </span>
                    </div>
                    {r.subject && (
                      <div className="text-sm text-[#3a3a3a] mt-2 flex items-start gap-1.5">
                        <FileText className="w-3.5 h-3.5 text-[#8a8a8a] mt-0.5 shrink-0" />
                        <span className="line-clamp-2">{r.subject}</span>
                      </div>
                    )}
                    {r.body && <p className="text-xs text-[#525252] mt-1 line-clamp-2">{r.body}</p>}
                    <div className="text-xs text-[#8a8a8a] mt-2 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(r.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")}
                    </div>
                  </div>
                  <span
                    className="text-xs px-2.5 py-1 rounded-full font-bold self-start whitespace-nowrap"
                    style={{ background: `${color}18`, color, border: `1px solid ${color}55` }}
                    data-testid={`my-request-${r.id}-status`}
                  >
                    {lang === "ar" ? st.ar : st.en}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Legacy service-orders (partner services) */}
          {orders.map((o) => (
            <div key={o.id} className="surface rounded-xl p-5" data-testid={`my-order-${o.id}`}>
              <div className="flex justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-display text-base">{o.code} — {o.partner_name}</div>
                  <div className="text-xs text-[#6b6b6b] mt-1">{o.partner_category} · {new Date(o.created_at).toLocaleString()}</div>
                  {o.notes && <p className="text-sm text-[#3a3a3a] mt-2">{o.notes}</p>}
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-[#c9a55c]/15 border border-[#c9a55c]/30 text-[#5a4615] font-semibold self-start">
                  {lang === "ar" ? LEGACY_STATUS_LABEL[o.status]?.ar : LEGACY_STATUS_LABEL[o.status]?.en}
                </span>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                {!["completed", "cancelled"].includes(o.status) && <button onClick={() => cancel(o.id)} className="btn-ghost text-xs text-red-700 border-red-300">{lang === "ar" ? "إلغاء الطلب" : "Cancel"}</button>}
                {o.status === "completed" && <button onClick={() => setReviewing(o)} className="btn-primary text-xs" data-testid={`review-${o.id}`}><Star className="w-3 h-3" /> {lang === "ar" ? "قيّم الخدمة" : "Review"}</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {reviewing && (
        <div className="fixed inset-0 bg-[#0a0a0a]/60 z-50 flex items-center justify-center p-4" onClick={() => setReviewing(null)}>
          <div className="bg-[#f1ecdf] rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-3">{lang === "ar" ? "قيّم الخدمة" : "Rate this service"}</h3>
            <div className="flex justify-center gap-1 mb-4">
              {[1,2,3,4,5].map(n => (
                <button key={n} onClick={() => setReview({ ...review, rating: n })} data-testid={`star-${n}`}>
                  <Star className={`w-10 h-10 ${n <= review.rating ? "text-[#c9a55c] fill-current" : "text-[#0a0a0a]/15"}`} />
                </button>
              ))}
            </div>
            <textarea className="input-dark w-full min-h-[100px]" placeholder={lang === "ar" ? "اكتب تعليقك (اختياري)" : "Comment (optional)"} value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} data-testid="review-comment" />
            <div className="flex gap-2 justify-end mt-3">
              <button onClick={() => setReviewing(null)} className="btn-ghost">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
              <button onClick={submitReview} className="btn-primary" data-testid="submit-review"><Send className="w-4 h-4" /> {lang === "ar" ? "إرسال" : "Submit"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
